import { useMemo, useState } from "react";

interface Event { createdAt: number; actionResult: "allow" | "warn" | "suggest_rewrite" | "block" }

interface Props {
  events: Event[];
}

const SERIES: Array<{ key: "allow" | "warned" | "blocked"; label: string; colour: string; }> = [
  { key: "blocked", label: "Blocked", colour: "var(--sg-red)" },
  { key: "warned", label: "Warned", colour: "var(--sg-amber)" },
  { key: "allow", label: "Allowed", colour: "var(--sg-accent)" },
];

export function ActivityChart({ events }: Props) {
  const { days, series } = useMemo(() => buildSeries(events), [events]);
  const [hover, setHover] = useState<number | null>(null);

  const W = 760, H = 180, PAD_L = 36, PAD_R = 18, PAD_T = 18, PAD_B = 26;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const maxY = Math.max(2, ...series.flatMap((s) => s.values));

  const x = (i: number) => PAD_L + (innerW * i) / Math.max(1, days.length - 1);
  const y = (v: number) => PAD_T + innerH * (1 - v / maxY);

  function pathFor(values: number[]): string {
    return values.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  }

  const ticks = buildYTicks(maxY);

  return (
    <div className="sg-chart-wrap">
      <div className="sg-chart-head">
        <div>
          <div className="sg-panel-title" style={{ margin: 0 }}>Activity</div>
          <div className="sg-muted" style={{ fontSize: 11, marginTop: 3 }}>Events by outcome · last 7 days</div>
        </div>
        <div className="sg-chart-legend">
          {SERIES.map((s) => (
            <span key={s.key} className="sg-chart-legend-item">
              <span className="sg-chart-legend-dot" style={{ background: s.colour }} />
              {s.label}
            </span>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="sg-chart-svg" role="img" aria-label="Activity line chart">
        {/* y ticks */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(t)} y2={y(t)} stroke="var(--sg-line-soft)" strokeWidth="1" strokeDasharray={t === 0 ? "0" : "2 3"} />
            <text x={PAD_L - 8} y={y(t) + 3} textAnchor="end" fontSize="9.5" fill="var(--sg-ink-4)" fontFamily="var(--sg-font-mono)">{t}</text>
          </g>
        ))}
        {/* x labels */}
        {days.map((d, i) => (
          <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="9.5" fill="var(--sg-ink-4)" fontFamily="var(--sg-font-mono)">
            {d.slice(0, 3).toUpperCase()}
          </text>
        ))}
        {/* series */}
        {series.map((s) => (
          <g key={s.key}>
            <path
              d={pathFor(s.values)}
              fill="none"
              stroke={s.colour}
              strokeWidth={s.key === "allow" ? 1.8 : 1.4}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={s.key === "allow" ? 1 : 0.85}
            />
            {s.values.map((v, i) => (
              <circle
                key={i}
                cx={x(i)} cy={y(v)} r={hover === i ? 3.5 : 2.2}
                fill={s.colour}
                stroke="var(--sg-bg-0)" strokeWidth="1.2"
              />
            ))}
          </g>
        ))}
        {/* hover zones */}
        {days.map((_, i) => (
          <rect
            key={i}
            x={x(i) - innerW / (2 * days.length)}
            y={PAD_T} width={innerW / days.length} height={innerH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
        {hover !== null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={PAD_T} y2={H - PAD_B} stroke="var(--sg-accent-line)" strokeWidth="1" strokeDasharray="2 2" />
          </g>
        )}
      </svg>
      {hover !== null && (
        <div className="sg-chart-tooltip">
          <div className="sg-chart-tooltip-day">{days[hover]}</div>
          {series.map((s) => (
            <div key={s.key} className="sg-chart-tooltip-row">
              <span className="sg-chart-legend-dot" style={{ background: s.colour }} />
              <span className="sg-chart-tooltip-label">{s.label}</span>
              <span className="sg-chart-tooltip-val">{s.values[hover]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function buildYTicks(max: number): number[] {
  const step = max <= 4 ? 1 : max <= 10 ? 2 : max <= 20 ? 5 : 10;
  const ticks: number[] = [];
  for (let i = 0; i <= max; i += step) ticks.push(i);
  if (ticks[ticks.length - 1] !== max) ticks.push(max);
  return ticks;
}

function buildSeries(events: Event[]) {
  const days: string[] = [];
  const buckets: Record<string, { allow: number; warned: number; blocked: number }> = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toLocaleDateString(undefined, { weekday: "short" });
    days.push(key);
    buckets[d.toDateString()] = { allow: 0, warned: 0, blocked: 0 };
  }
  for (const e of events) {
    const d = new Date(e.createdAt);
    d.setHours(0, 0, 0, 0);
    const key = d.toDateString();
    if (!buckets[key]) continue;
    if (e.actionResult === "allow") buckets[key].allow++;
    else if (e.actionResult === "block") buckets[key].blocked++;
    else buckets[key].warned++;
  }
  const keys = Object.keys(buckets);
  const series = [
    { key: "blocked" as const, label: "Blocked", colour: "var(--sg-red)", values: keys.map((k) => buckets[k].blocked) },
    { key: "warned"  as const, label: "Warned",  colour: "var(--sg-amber)", values: keys.map((k) => buckets[k].warned) },
    { key: "allow"   as const, label: "Allowed", colour: "var(--sg-accent)", values: keys.map((k) => buckets[k].allow) },
  ];
  return { days, series };
}
